import { useCallback, useEffect, useRef } from 'react';
import { writeFetch, getSession } from '../api';

const NOTIF_TABLE  = 'notifications_1741164676';
const EVENTS_TABLE = 'family_network_events_1749100000';
const FAMILY_TABLE = 'user_family_members_1749000000';

// ── Dispatch a single in-app notification ────────────────────────────────────
async function pushNotification({ userId, title, message, type = 'family_tree' }) {
  try {
    await writeFetch(`create/${NOTIF_TABLE}`, {
      method: 'POST',
      body: JSON.stringify({
        id: `notif-fn-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        user_id: userId,
        owner_id: userId,
        title,
        message,
        type,
        is_read: 0,
        created_at: new Date().toISOString(),
      }),
    });
  } catch { /* silent */ }
}

// ── Public: fire when a new family member is added / linked to a memorial ───
export async function notifyFamilyMemberAdded({ ownerUserId, memberName, relationship, memorialName }) {
  const eventId = `fne-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  try {
    await writeFetch(`create/${EVENTS_TABLE}`, {
      method: 'POST',
      body: JSON.stringify({
        id: eventId,
        owner_id: ownerUserId,
        event_type: 'member_added',
        family_member_name: memberName,
        relationship,
        memorial_name: memorialName || null,
        is_notified: 0,
        created_at: new Date().toISOString(),
      }),
    });

    const title   = '👨‍👩‍👧 New Family Member Added';
    const message = memorialName
      ? `${memberName} (${relationship}) has been added to your family network and linked to "${memorialName}".`
      : `${memberName} (${relationship}) has been added to your family network.`;

    await pushNotification({ userId: ownerUserId, title, message, type: 'family_tree' });
  } catch { /* silent */ }
}

// ── Public: fire when a life event (bio/years) is updated on a family member ─
export async function notifyLifeEventUpdated({ ownerUserId, memberName, lifeEventType, detail }) {
  const eventId = `fne-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  try {
    await writeFetch(`create/${EVENTS_TABLE}`, {
      method: 'POST',
      body: JSON.stringify({
        id: eventId,
        owner_id: ownerUserId,
        event_type: 'life_event_updated',
        family_member_name: memberName,
        life_event_type: lifeEventType,
        life_event_detail: detail || null,
        is_notified: 0,
        created_at: new Date().toISOString(),
      }),
    });

    const title   = '📅 Family Life Event Updated';
    const message = `A life event for ${memberName} has been recorded: ${detail || lifeEventType}.`;

    await pushNotification({ userId: ownerUserId, title, message, type: 'family_tree' });
  } catch { /* silent */ }
}

// ── Public: fire when a family member is linked to a memorial tree ────────────
export async function notifyMemorialTreeLinked({ ownerUserId, memberName, memorialName }) {
  const eventId = `fne-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  try {
    await writeFetch(`create/${EVENTS_TABLE}`, {
      method: 'POST',
      body: JSON.stringify({
        id: eventId,
        owner_id: ownerUserId,
        event_type: 'tree_linked',
        family_member_name: memberName,
        memorial_name: memorialName,
        is_notified: 0,
        created_at: new Date().toISOString(),
      }),
    });

    const title   = '🌳 Family Tree Connection Made';
    const message = `${memberName} has been connected to the memorial tree for "${memorialName}".`;

    await pushNotification({ userId: ownerUserId, title, message, type: 'family_tree' });
  } catch { /* silent */ }
}

// ── Hook: poll for unprocessed events & show notifications ────────────────────
export function useFamilyNetworkNotifications() {
  const isMounted = useRef(true);
  useEffect(() => () => { isMounted.current = false; }, []);

  const poll = useCallback(async () => {
    const user = getSession();
    if (!user) return;
    try {
      const res  = await writeFetch(
        `read/${EVENTS_TABLE}?owner_id=${user.id}&is_notified=0&limit=20`
      );
      const data = await res.json();
      if (!isMounted.current || data.status !== 'success') return;

      const pending = Array.isArray(data.data) ? data.data : [];
      if (!pending.length) return;

      // Mark all as notified in parallel (fire-and-forget)
      pending.forEach(ev => {
        writeFetch(`update/${EVENTS_TABLE}/${ev.id}`, {
          method: 'PUT',
          body: JSON.stringify({ is_notified: 1 }),
        }).catch(() => {});
      });
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    poll();
    const id = setInterval(poll, 30000);
    return () => clearInterval(id);
  }, [poll]);
}